import { createAssistantStream } from 'assistant-stream';

import { api } from '@/lib/api-client';

import { createSession } from '../api/create-session';
import { deleteSession } from '../api/delete-session';
import { listSessions } from '../api/list-sessions';

import type { SessionItem } from '../types';
import type { RemoteThreadListAdapter, ThreadMessage } from '@assistant-ui/react';

interface RemoteThreadMetadata {
  readonly status: 'regular' | 'archived';
  readonly remoteId: string;
  readonly externalId?: string | undefined;
  readonly title?: string | undefined;
}

/**
 * Module-level registry for pending session IDs created inline by onNew.
 * Key: `${orgId}:${projectId}`, value: sessionId.
 * Allows adapter.initialize() to reuse a session created by onNew for the same project
 * instead of creating a duplicate when the runtime fires the "initialize" event.
 */
const pendingSessionRegistry = new Map<string, SessionItem>();

export const pendingSessionRegistryUtils = {
  set(orgId: string, projectId: string, session: SessionItem): void {
    pendingSessionRegistry.set(`${orgId}:${projectId}`, session);
  },
  consume(orgId: string, projectId: string): SessionItem | null {
    const key = `${orgId}:${projectId}`;
    const value = pendingSessionRegistry.get(key) ?? null;
    if (value) pendingSessionRegistry.delete(key);
    return value;
  },
};

export function createThreadListAdapter(params: {
  orgId: string;
  projectId: string;
}): RemoteThreadListAdapter {
  return {
    async list(): Promise<{ threads: RemoteThreadMetadata[] }> {
      const sessions = await listSessions(params.orgId, params.projectId);
      const threads = sessions
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .map(
          (s): RemoteThreadMetadata => ({
            status: 'regular',
            remoteId: s.id,
            title:
              s.title ??
              new Date(s.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }),
          }),
        );
      return { threads };
    },

    async initialize(_localId: string): Promise<{ remoteId: string; externalId: undefined }> {
      const pending = pendingSessionRegistryUtils.consume(params.orgId, params.projectId);
      if (pending) {
        return { remoteId: pending.id, externalId: undefined };
      }
      const session = await createSession(params.orgId, params.projectId, 'new sessions');
      return { remoteId: session.id, externalId: undefined };
    },

    async delete(remoteId: string): Promise<void> {
      await deleteSession(params.orgId, params.projectId, remoteId);
    },

    async archive(remoteId: string): Promise<void> {
      // D-26: archive maps to delete — no soft archive in v3.1
      await deleteSession(params.orgId, params.projectId, remoteId);
    },

    unarchive(_remoteId: string): Promise<void> {
      return Promise.resolve();
    },

    async rename(remoteId: string, newTitle: string): Promise<void> {
      await api.patch(
        `/orgs/${params.orgId}/projects/${params.projectId}/ai-agent/sessions/${remoteId}`,
        { title: newTitle },
      );
    },

    // eslint-disable-next-line @typescript-eslint/require-await
    async generateTitle(remoteId: string, unstable_messages: ThreadMessage[]) {
      return createAssistantStream(async (controller) => {
        let title = 'new sessions';
        const firstMessage = unstable_messages[0];
        const firstContent = Array.isArray(firstMessage.content)
          ? firstMessage.content[0]
          : undefined;
        if (firstContent?.type === 'text' && typeof firstContent.text === 'string') {
          title =
            firstContent.text.length < 100 ? firstContent.text : firstContent.text.slice(0, 97);
          await api.patch(
            `/orgs/${params.orgId}/projects/${params.projectId}/ai-agent/sessions/${remoteId}`,
            { title },
          );
        }
        controller.appendText(title);
      });
    },

    async fetch(threadId: string): Promise<RemoteThreadMetadata> {
      const sessions = await listSessions(params.orgId, params.projectId);
      const s = sessions.find((session) => session.id === threadId);
      if (!s) throw new Error(`Session ${threadId} not found`);
      return { status: 'regular', remoteId: s.id, title: s.title };
    },
  };
}
